/**
 * LeadIntake — PUBLIC web form at /intake. No login required. Three parts:
 *   1. Contact & building info
 *   2. Request type (radio): Free Roof Inspection OR fee-based Full Roof &
 *      System Analysis
 *   3. Roof details (system / deck / substrate) — shown only for the analysis
 * Lands in the CRM as Contact + Property + Deal (status "Lead", source
 * "Web Form", title tagged [FULL ANALYSIS] when applicable). Honeypot guards bots.
 */
import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Send, ClipboardCheck, Microscope, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const F = "w-full h-11 px-3 border border-zinc-300 bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4C81]";
const L = "block text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-700 mb-1.5";

const ROOF_SYSTEMS = [
  "TPO", "PVC", "EPDM", "Ballasted EPDM", "Built Up", "Modified Bitumen",
  "Modified w/ Ballast", "Metal", "FARM (Fluid Applied Reinforced Membrane)",
  "Silicone", "Silicone w/ Granules", "Acrylic", "Tile", "Shingle",
  "Other", "Unknown",
];
const ROOF_DECKS = ["Concrete", "Steel", "Wood Beams", "Other", "Unknown"];
const SUBSTRATES = ["Plywood/OSB", "DensDeck", "ISO only", "Other", "Unknown"];
const ROOF_ACCESS = ["Roof hatch (interior)", "Fixed exterior ladder", "Exterior stairs", "Ladder required — no built-in access", "Other", "Unknown"];

const empty = {
  name: "", company: "", email: "", phone: "",
  property_address: "", city: "", state: "", zip_code: "",
  roof_sqft: "", message: "", website_hp: "",
  roof_access: "", roof_access_other: "",
  request_type: "inspection",
  roof_system: "", roof_system_other: "",
  roof_deck: "", roof_deck_other: "",
  substrate: "", substrate_other: "",
};

const SectionTitle = ({ n, children }) => (
  <div className="flex items-center gap-2 pt-2">
    <span className="w-6 h-6 rounded-full bg-[#0F4C81] text-white text-xs font-black flex items-center justify-center">{n}</span>
    <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-800">{children}</span>
    <span className="flex-1 h-px bg-zinc-200" />
  </div>
);

const DetailSelect = ({ label, value, other, options, onValue, onOther, testId }) => (
  <div>
    <label className={L}>{label}</label>
    <select data-testid={testId} className={F} value={value} onChange={(e) => onValue(e.target.value)}>
      <option value="">Select…</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
    {value === "Other" && (
      <input
        data-testid={`${testId}-other`}
        className={`${F} mt-2`}
        value={other}
        onChange={(e) => onOther(e.target.value)}
        placeholder={`Tell us about your ${label.toLowerCase()}`}
      />
    )}
  </div>
);

export default function LeadIntake() {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const embedded = typeof window !== "undefined" &&
    (window.self !== window.top || new URLSearchParams(window.location.search).get("embed") === "1");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setV = (k) => (v) => setForm({ ...form, [k]: v });
  const isAnalysis = form.request_type === "analysis";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/public/lead-intake`, {
        ...form,
        roof_sqft: parseFloat(form.roof_sqft) || 0,
      });
      setDone(true);
    } catch (err) {
      alert("Something went wrong — please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  const radioCard = (value, Icon, title, blurb, testId) => (
    <label
      data-testid={testId}
      className={`flex gap-3 p-4 border-2 rounded-sm cursor-pointer transition-colors ${form.request_type === value ? "border-[#0F4C81] bg-[#EDF3F9]" : "border-zinc-200 hover:border-zinc-400"}`}
    >
      <input
        type="radio"
        name="request_type"
        value={value}
        checked={form.request_type === value}
        onChange={set("request_type")}
        className="mt-1 accent-[#0F4C81]"
      />
      <div className="min-w-0">
        <div className="flex items-center gap-2 font-bold text-sm text-zinc-900">
          <Icon className="w-4 h-4 text-[#0F4C81]" /> {title}
        </div>
        <p className="text-xs text-zinc-600 mt-1 leading-relaxed">{blurb}</p>
      </div>
    </label>
  );

  return (
    <div className={embedded ? "min-h-screen bg-white flex flex-col items-center" : "min-h-screen bg-white flex flex-col items-center px-4 py-6"} data-testid="lead-intake-page">
      {!embedded && <img src="/sealtech-logo.png" alt="SealTech Building Solutions" className="h-14 w-auto mb-4 bg-white rounded-sm p-2" />}
      <div className={`w-full max-w-5xl bg-white rounded-sm overflow-hidden ${embedded ? "border border-zinc-200" : "border border-zinc-200 shadow-lg"}`}>
        <div className="px-8 py-4 bg-[#0F4C81] text-white">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#A9C7E3]">SealTech Building Solutions</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight mt-1">Roof Inquiry</h1>
          <p className="text-sm text-[#CBDDEE] mt-1">Tell us about your building — we&apos;ll reach out within one business day.</p>
        </div>

        {done ? (
          <div className="p-12 text-center" data-testid="intake-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-black tracking-tight">Thanks — we got it!</h2>
            <p className="text-sm text-zinc-600 mt-2">
              Your request is in our system. A SealTech estimator will contact you shortly
              {isAnalysis ? " with the analysis fee for your building." : "."}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 sm:p-8" data-testid="intake-form">
            {/* Honeypot — hidden from humans, bots fill it and get dropped */}
            <input type="text" value={form.website_hp} onChange={set("website_hp")} tabIndex={-1} autoComplete="off" className="absolute -left-[9999px] top-0 h-0 w-0 opacity-0" aria-hidden="true" />

            <div className="grid grid-cols-1 min-[700px]:grid-cols-2 gap-x-10 gap-y-5">
              {/* LEFT — contact & building */}
              <div className="space-y-4">
            <SectionTitle n={1}>Contact &amp; Building</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={L}>Your Name *</label>
                <input data-testid="intake-name" required className={F} value={form.name} onChange={set("name")} />
              </div>
              <div>
                <label className={L}>Company</label>
                <input data-testid="intake-company" className={F} value={form.company} onChange={set("company")} />
              </div>
              <div>
                <label className={L}>Email</label>
                <input data-testid="intake-email" type="email" className={F} value={form.email} onChange={set("email")} />
              </div>
              <div>
                <label className={L}>Phone</label>
                <input data-testid="intake-phone" className={F} value={form.phone} onChange={set("phone")} />
              </div>
            </div>
            <div>
              <label className={L}>Property Address</label>
              <input data-testid="intake-address" className={F} value={form.property_address} onChange={set("property_address")} placeholder="Street address of the building" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
              <div className="sm:col-span-3">
                <label className={L}>City</label>
                <input data-testid="intake-city" className={F} value={form.city} onChange={set("city")} />
              </div>
              <div className="sm:col-span-1">
                <label className={L}>State</label>
                <input data-testid="intake-state" className={F} value={form.state} onChange={set("state")} placeholder="CO" maxLength={2} />
              </div>
              <div className="sm:col-span-2">
                <label className={L}>ZIP</label>
                <input data-testid="intake-zip" className={F} value={form.zip_code} onChange={set("zip_code")} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={L}>Approx. Roof Size (SF)</label>
                <input data-testid="intake-sqft" type="number" min="0" className={F} value={form.roof_sqft} onChange={set("roof_sqft")} placeholder="e.g. 12000" />
              </div>
              <DetailSelect label="Roof Access" options={ROOF_ACCESS} value={form.roof_access} other={form.roof_access_other} onValue={setV("roof_access")} onOther={setV("roof_access_other")} testId="intake-roof-access" />
            </div>
            <div>
              <label className={L}>Any question, or anything you want to share?</label>
              <textarea data-testid="intake-message" rows={3} className="w-full px-3 py-2 border border-zinc-300 bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4C81]" value={form.message} onChange={set("message")} placeholder="Leaks, ponding, re-coat quote, maintenance plan..." />
            </div>
              </div>

              {/* RIGHT — request type + details + submit */}
              <div className="space-y-4">
            <SectionTitle n={2}>What do you need?</SectionTitle>
            <div className="grid grid-cols-1 gap-3">
              {radioCard(
                "inquiry", MessageSquare, "Inquiry Only",
                "Just have a question and want to talk.",
                "intake-type-inquiry"
              )}
              {radioCard(
                "inspection", ClipboardCheck, "Free Roof Inspection",
                "A SealTech estimator walks your roof, documents conditions with photos, and gives you honest recommendations. No cost, no obligation.",
                "intake-type-inspection"
              )}
              {radioCard(
                "analysis", Microscope, "Full Roof & System Analysis (fee-based)",
                "An in-depth evaluation of your roof system, deck, and substrate — including moisture assessment — delivered as a written report. We'll reply with the analysis fee for your building before any work begins.",
                "intake-type-analysis"
              )}
            </div>

            {isAnalysis && (
              <>
                <SectionTitle n={3}>Roof Details</SectionTitle>
                <p className="text-xs text-zinc-500 -mt-2">Best guesses are fine — choose Unknown if you&apos;re not sure.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="intake-roof-details">
                  <DetailSelect label="Type of Roof System" options={ROOF_SYSTEMS} value={form.roof_system} other={form.roof_system_other} onValue={setV("roof_system")} onOther={setV("roof_system_other")} testId="intake-roof-system" />
                  <DetailSelect label="Type of Roof Deck" options={ROOF_DECKS} value={form.roof_deck} other={form.roof_deck_other} onValue={setV("roof_deck")} onOther={setV("roof_deck_other")} testId="intake-roof-deck" />
                  <DetailSelect label="Type of Substrate" options={SUBSTRATES} value={form.substrate} other={form.substrate_other} onValue={setV("substrate")} onOther={setV("substrate_other")} testId="intake-substrate" />
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={sending || !form.name.trim()}
              data-testid="intake-submit"
              className="w-full inline-flex items-center justify-center gap-2 h-12 bg-[#0F4C81] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#0C3E6A] rounded-sm disabled:opacity-50 transition-colors"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Sending…" : isAnalysis ? "Request Analysis Quote" : form.request_type === "inquiry" ? "Send My Inquiry" : "Request My Free Inspection"}
            </button>
              </div>
            </div>
          </form>
        )}
      </div>
      {!embedded && <div className="text-[11px] text-zinc-500 mt-6">© {new Date().getFullYear()} SealTech Building Solutions</div>}
    </div>
  );
}
